"use client";

import { useEffect } from "react";

const TARGETS = [
  "main > *",
  "main > * > *",
  "main > * > * > *",
  "section",
  "article",
  "[class*='-card']",
  "[class*='-section']",
].join(",");
const SKIP = "dialog, dialog *, .toast-viewport, .toast-viewport *, header, nav, aside, [data-no-reveal], [data-no-reveal] *, .reveal";

// Fades blocks in every time they enter the viewport, in both scroll directions, on every page.
export function ScrollReveal() {
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          if (!el.isConnected) continue;
          el.dataset.reveal = entry.isIntersecting ? "shown" : "hidden";
        }
      },
      { threshold: 0, rootMargin: "0px 0px -48px 0px" },
    );
    const seen = new WeakSet<Element>();
    const scan = () => {
      document.querySelectorAll<HTMLElement>(TARGETS).forEach((el) => {
        if (seen.has(el) || el.matches(SKIP)) return;
        const position = getComputedStyle(el).position;
        if (position === "fixed" || position === "sticky") return;
        seen.add(el);
        // Content already on screen is shown straight away; it hides only once it scrolls out.
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 48 && rect.bottom > 0) el.dataset.reveal = "shown";
        observer.observe(el);
      });
    };
    scan();
    let frame = 0;
    const mutations = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(scan);
    });
    mutations.observe(document.body, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(frame);
      mutations.disconnect();
      observer.disconnect();
    };
  }, []);
  return null;
}
